import {
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';

import {
  GeneralAttachmentModel,
  MaintananceLevelModel,
  MaintenanceCriteriaModel,
  MaintenanceCriteriaSettingModel,
  MasteryCriteriaModel,
  PatientModel,
  PatientPlaylistModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientTargetStatus,
  PromptCodeModel,
  PromptScheduleModel,
  SkillAcquisitionObjectiveModel,
  SkillAreaModel,
  SubTargetsSimpleModel,
  TargetFailOption,
  TargetType,
  TreatmentGoalModel,
  AlarmOption,
  FluencyRateTimerMode,
  PatientProgramStatus,
  Permissions,
} from '../../../core/api/models';
import { Utils } from '../../../core/utils';
import { AlertService } from '../../../shared/services/alert.service';
import {
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { formatDate } from '@angular/common';
import { SupervisorBaseComponent } from '../../supervisor-base.component';
import {
  getAlarmOptions,
  getAlarmSoundOptions,
  getPatientTargetStatuses,
} from '../../../shared/utils/utils';
import { forkJoin } from 'rxjs';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { AuthService } from '../../../auth/auth.service';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';
import { AddAttachmentModalComponent } from '../../../shared/components/add-attachment/add-attachment-modal/add-attachment-modal.component';
import { AttachmentViewComponent } from '../../../shared/components/attachment-view/attachment-view.component';

// CKEditor imports
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';

@Component({
  selector: 'app-add-target',
  templateUrl: './add-target.component.html',
  styleUrl: './add-target.component.css',
  standalone: false,
})
export class AddTargetComponent
  extends SupervisorBaseComponent
  implements OnInit
{
  private readonly supportedMaintenanceLevelNames = new Set([
    'Daily',
    'Weekly',
    'Monthly',
  ]);

  @Input() selectedPatient: PatientModel | null = null;
  @Input() addingTargetToProgramID = signal<number | null>(null);
  @Output() close = new EventEmitter<boolean>();

  selectedPrograms: PatientProgramModel | null = null;

  selectedPlaylists: PatientPlaylistModel | null = null;

  promptSchedules: PromptScheduleModel[] = [];
  selectedPromptSchedule: any | null = null; //PromptScheduleModel | null = null;

  treatmentGoals: TreatmentGoalModel[] = [];

  selectedSubTargets: PatientTargetModel[] = [];

  // Add new property for newly created subtargets
  newSubTargets: { TargetName: string; ForceOpen: boolean }[] = [];

  skillAreas: SkillAreaModel[] = [];
  selectedSkillAreaID = signal<number | null>(null);

  showAllPrograms = signal<boolean>(false);

  targetTypes: any[] = [];

  // Attachment functions
  isLoadingAttachments: boolean = false;
  showAddAttachments: boolean = false;
  doShowAttachmentView: boolean = false;
  addAttachmentView: string = 'patientInstructions';
  targetID: number = 0;
  attachmentList: GeneralAttachmentModel[] = [];
  selectedAttachment: GeneralAttachmentModel | null = null;

  targetStatuses: any[] = getPatientTargetStatuses();
  selectedStatus = PatientTargetStatus.Ready;
  maintenanceCriterias: { text: string; value: number }[] = [];
  maintenanceCriteriaModels: MaintenanceCriteriaModel[] = [];
  effectiveMaintenanceCriteriaModels: MaintenanceCriteriaModel[] = [];
  maintenanceCriteriaSettings: MaintenanceCriteriaSettingModel = {};
  maintenanceLevels: MaintananceLevelModel[] = [];
  defaultMasteryCriteria: MasteryCriteriaModel = {
    ResponsePercentage: 80,
    ConsecutiveDays: 5,
    FirstTrialIndependent: true,
  };
  hasPatientSpecificMasteryCriteria = false;
  hasPatientSpecificMaintenanceCriteria = false;
  isUsingLegacyMaintenanceCriteria = false;
  inheritedMaintenanceCriteriaIdForDisplay: number | null = null;
  masteryDestinationOptions = [
    { label: 'Maintenance', value: true },
    { label: 'Fluent', value: false },
  ];
  afterMasteryHandlingOptions = [
    { label: 'Automatic', value: true },
    { label: 'Manual', value: false },
  ];

  promptCodes: PromptCodeModel[] = [];

  availableTargets: PatientTargetModel[] = [];

  showingAvailableTargets = false;

  skillAcquisitionGoals: (SkillAcquisitionObjectiveModel & {
    DropdownLabel: string;
  })[]
    | undefined;

  selectedAlarmOption = signal<AlarmOption>(AlarmOption.NoAlarm);
  selectedAlarmSound = signal<number | null>(null);

  alarmOptions: SelectItem[] = getAlarmOptions();
  alarmSounds: SelectItem[] = getAlarmSoundOptions();
  fluencyRateTimerModeOptions = [
    { label: 'Fixed Duration', value: FluencyRateTimerMode.FixedDuration },
    { label: 'Open-ended', value: FluencyRateTimerMode.OpenEnded },
  ];

  // Using the ClassicEditor from the imported CKEditor module
  public Editor = Editor;

  initialInstructions: string = '';

  // CKEditor configuration
  public editorConfig = editorConfig;

  availablePrograms = computed<SelectItem[]>(() => {
    let programs = this.supervisorHelperService.programs();
    let skillAreaID = this.selectedSkillAreaID();

    if (skillAreaID != null) {
      programs = programs.filter(
        (program) => program.SkillAreaID === skillAreaID
      );
    }

    programs = programs.filter((program) => !program.IsDeleted);

    if (!this.showAllPrograms()) {
      programs = programs.filter(
        (program) =>
          (!program.IsArchived &&
            program.ProgramStatus == PatientProgramStatus.Aquisition) ||
          this.targetForm
            .get('PatientProgramIDs')
            ?.value?.find((id) => id == program.ID)
      );
    }

    return programs.map((program) => {
      return {
        label: program.ProgramName ?? 'No Name for Program',
        value: program.ID,
      };
    });
  });

  typingTimer: number | undefined;
  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );

    // tour update events
    this.targetForm.get('targetName')?.valueChanges.subscribe((value) => {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      this.typingTimer = window.setTimeout(() => {
        const event = new Event('name-entered');
        document.getElementById('addTargetForm')?.dispatchEvent(event);
      }, 500);
    });

    this.targetForm
      .get('PatientProgramIDs')
      ?.valueChanges.subscribe((value) => {
        if (value != null && value.length > 0) {
          const event = new Event('programs-selected');
          document.getElementById('addTargetForm')?.dispatchEvent(event);

          if (!this.targetForm.get('SkillAreaID')?.value) {
            // select the skillarea based on the selected program
            const selectedProgram = this.supervisorHelperService
              .programs()
              .find((p) => value.includes(p.ID!));
            if (selectedProgram) {
              this.targetForm
                .get('SkillAreaID')
                ?.setValue(selectedProgram.SkillAreaID ?? null);
            }
          }
        }
      });

    this.targetForm.get('targetType')?.valueChanges.subscribe((value) => {
      if (value != null) {
        const event = new Event('target-type-selected');
        document.getElementById('addTargetForm')?.dispatchEvent(event);

        if (value === TargetType.TaskAnalysis) {
          this.availableTargets = this.getFilteredTargets().filter(
            (t) =>
              t.TargetType == TargetType.DiscreteTrialTeaching ||
              t.TargetType == TargetType.Echoic
          );
        } else if (value === TargetType.GroupedFrequency) {
          this.availableTargets = this.getFilteredTargets().filter(
            (t) => t.TargetType == TargetType.Frequency
          );
        } else if (value === TargetType.ConditionalDiscrimination) {
          this.availableTargets = this.getFilteredTargets().filter(
            (t) =>
              t.TargetType != TargetType.ConditionalDiscrimination &&
              t.TargetType != TargetType.GroupedFrequency &&
              t.TargetType != TargetType.TaskAnalysis
          );
        } else {
          this.availableTargets = [];
        }

        this.availableTargets = this.availableTargets
          .filter((t) => !this.selectedSubTargets.some((st) => st.ID === t.ID))
          .sort((a, b) => {
            const aIndex = this.selectedSubTargets.findIndex(
              (t) => t.ID === a.ID
            );
            const bIndex = this.selectedSubTargets.findIndex(
              (t) => t.ID === b.ID
            );
            if (aIndex == -1 && bIndex == -1) return 0;
            if (aIndex == -1) return 1;
            if (bIndex == -1) return -1;
            return aIndex - bIndex;
          });

        // When target type changes to Currency during creation, sync CurrentBalance with StartingBalance
        if (value === TargetType.Currency && !this.currentTarget?.ID) {
          const startingBalance = this.targetForm.get('StartingBalance')?.value;
          if (startingBalance != null && startingBalance !== 0) {
            this.targetForm.get('CurrentBalance')?.setValue(startingBalance, { emitEvent: false });
          }
        }

        if (value === TargetType.FluencyRate && this.targetForm.get('FluencyRateTimerMode')?.value == null) {
          this.targetForm.get('FluencyRateTimerMode')?.setValue(FluencyRateTimerMode.FixedDuration);
        }

        this.updateAlarmControlsForTargetType();
      }
    });

    this.targetForm
      .get('FluencyRateTimerMode')
      ?.valueChanges.subscribe(() => {
        this.updateDurationValidators(this.targetForm.controls.targetType.value);
        this.updateAlarmControlsForTargetType();
      });

    // When creating a new currency target, sync CurrentBalance with StartingBalance
    this.targetForm.get('StartingBalance')?.valueChanges.subscribe((startingBalance) => {
      // Only sync during creation (when currentTarget has no ID) and when target type is Currency
      // Only sync if starting balance is different than 0
      if (!this.currentTarget?.ID && 
          this.targetForm.get('targetType')?.value === TargetType.Currency &&
          startingBalance != null &&
          startingBalance !== 0) {
        // Set CurrentBalance to the same value as StartingBalance
        this.targetForm.get('CurrentBalance')?.setValue(startingBalance, { emitEvent: false });
      }
    });
  }

  targetForm = new FormGroup({
    targetName: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.maxLength(70)],
    }),
    goalName: new FormControl<string | null>(null, {
      validators: [Validators.maxLength(255)],
    }),
    CurrencySymbol: new FormControl<string | null>(null),
    StartingBalance: new FormControl<number | null>(0),
    CurrentBalance: new FormControl<number | null>(null),
    CanBeNegative: new FormControl<boolean | null>(true),
    BSP_TreatmentGoalID: new FormControl<number | null>(null, {
      validators: [],
    }),
    discriminativeStimulus: new FormControl<string | null>(null, {
      validators: [Validators.maxLength(200)],
    }),
    desiredTrials: new FormControl<number | null>(1, {
      validators: [Validators.min(0), Validators.max(100)],
    }),
    targetType: new FormControl<TargetType>(TargetType.Anecdotal, {
      validators: [Validators.required],
    }),
    duration: new FormControl<number | null>(0),
    FluencyRateTimerMode: new FormControl<FluencyRateTimerMode>(FluencyRateTimerMode.FixedDuration),
    targetStatus: new FormControl<PatientTargetStatus>(
      PatientTargetStatus.Ready
    ),
    PatientProgramIDs: new FormControl<number[] | null>(null),
    PatientPlaylistIDs: new FormControl<number[] | null>(null),
    PromptScheduleID: new FormControl<number | null>(null),
    instructions: new FormControl<string | null>(null),
    IsBackward: new FormControl<boolean | null>(false),
    isFavorite: new FormControl<boolean | null>(false),
    isSpecialFavorite: new FormControl<boolean | null>(false),
    SubTargetIDs: new FormControl<number[] | null>(null),
    CustomPromptCodeIDs: new FormControl<number[] | null>(null),
    CustomMasteryCriteria: new FormControl<boolean>(false),
    FirstTrialMustBeIndependent: new FormControl<boolean | null>(false),
    consecutiveDays: new FormControl<number | null>(null),
    responsePercentage: new FormControl<number | null>(null),
    DateBaselined: new FormControl<string | null>(null),
    DateClosed: new FormControl<string | null>(null),
    DateMastered: new FormControl<string | null>(null),
    DateOpened: new FormControl<string | null>(null),
    QuantityUnit: new FormControl<string | null>(null),
    SkillAreaID: new FormControl<number | null>(null),
    ShouldMasterAutomatic: new FormControl<boolean>(false),
    ShouldHandleAfterMasteryAutomatic: new FormControl<boolean>(true),
    ShouldMoveToMaintenanceAfterMastery: new FormControl<boolean>(true),
    MaintenanceCriteriaID: new FormControl<number | null>(null),
    OpenSubTargets: new FormControl<boolean>(false),
    BSP_SkillAcquisitionGoalID: new FormControl<number | null>(null),
    SkillAcquisitionGoal_Other: new FormControl<string | null>(null),
    AlarmOption: new FormControl<AlarmOption>(AlarmOption.NoAlarm, {
      validators: [Validators.required],
    }),
    AlarmSoundOption: new FormControl<number | null>(null),
  });

  currentTarget?: PatientTargetModel;

  titleName: string = 'Add Target';
  actionName: string = 'Add';

  ngOnInit(): void {
    this.currentTarget = {
      PatientID: this.selectedPatient?.ID,
      TargetFailOption: TargetFailOption.MarkAsFailed,
    };

    this.loadBaseData(this.selectedPatient?.ID!, () => {
      this.isLoading = true;
      this.promptSchedules = this.supervisorHelperService
        .promptSchedules()
        .filter((promptSchedule) => !promptSchedule.IsDeleted);
      this.treatmentGoals = this.supervisorHelperService.treatmentGoals();
      this.isLoading = false;
      this.skillAreas = this.supervisorHelperService
        .skillAreas()
        .filter((skillArea) => !skillArea.IsDeleted);

      this.loadMasteryMaintenanceAndPromptCodes();

      this.loadTargetTypes();
      this.updatesFormValidators();
      // Subscribe to target type changes to update duration validators
      this.targetForm.controls.targetType.valueChanges.subscribe(
        (targetType) => {
          if (targetType != null) {
            this.updateDurationValidators(targetType);
          }
        }
      );

      // Full objective objects from the latest BSP; objectives without an
      // objective description are intentionally NOT filtered out anymore.
      const bspObjectives = [
        ...this.supervisorHelperService.skillAcquisitionGoals(),
      ];

      if (bspObjectives.find((goal) => goal.Goal === 'Other') == null) {
        bspObjectives.push({
          ID: null,
          Goal: 'Other',
          Program: '',
        });
      }

      this.skillAcquisitionGoals = bspObjectives.map((goal, index) => ({
        ...goal,
        DropdownLabel:
          goal.Goal === 'Other'
            ? 'Other'
            : `#${index + 1} - ${goal.Goal || 'No Objective Description'} - ${
                goal.SkillArea || '-'
              } - ${goal.Program || '-'}`,
      }));

      // EDIT LOGIC
      if (this.supervisorHelperService.targetToEdit() != null) {
        this.titleName = 'Edit Target';
        this.actionName = 'Save';
        this.currentTarget = this.supervisorHelperService.targetToEdit()!;

        const subTargets = this.currentTarget?.SubTargets ?? [];

        if (this.currentTarget?.TargetType === TargetType.TaskAnalysis) {
          this.availableTargets = this.getFilteredTargets().filter(
            (t) =>
              t.TargetType == TargetType.DiscreteTrialTeaching ||
              t.TargetType == TargetType.Echoic
          );
        } else if (
          this.currentTarget?.TargetType === TargetType.GroupedFrequency
        ) {
          this.availableTargets = this.getFilteredTargets().filter(
            (t) => t.TargetType == TargetType.Frequency
          );
        } else {
          this.availableTargets = this.getFilteredTargets();
        }

        const selectedSubTargets = subTargets;
        //Order availableTargets according to the selectedSubTargets order
        this.availableTargets = this.availableTargets
          .filter((t) => !subTargets.some((st) => st.ID === t.ID))
          .sort((a, b) => {
            const aIndex = selectedSubTargets.findIndex((t) => t.ID === a.ID);
            const bIndex = selectedSubTargets.findIndex((t) => t.ID === b.ID);
            if (aIndex == -1 && bIndex == -1) return 0;
            if (aIndex == -1) return 1;
            if (bIndex == -1) return -1;
            return aIndex - bIndex;
          });

        this.selectedSubTargets = selectedSubTargets;

        this.targetID = this.currentTarget?.ID ?? 0;

        this.targetForm.patchValue({
          targetName: this.currentTarget.TargetName,
          discriminativeStimulus: this.currentTarget.DiscriminativeStimulus,
          desiredTrials: this.currentTarget.DesiredTrials,
          CurrencySymbol: this.currentTarget.CurrencySymbol,
          StartingBalance: this.currentTarget.StartingBalance,
          CurrentBalance: this.currentTarget.CurrentBalance,
          targetType: this.currentTarget.TargetType,
          duration: this.secondsToMinutes(this.currentTarget.Duration ?? 0),
          FluencyRateTimerMode:
            this.currentTarget.FluencyRateTimerMode ??
            FluencyRateTimerMode.FixedDuration,
          targetStatus: this.currentTarget.TargetStatus,
          PatientProgramIDs: this.currentTarget.PatientProgramIDs,
          PatientPlaylistIDs: this.currentTarget.PatientPlaylistIDs,
          PromptScheduleID: this.currentTarget.PromptScheduleID,
          instructions: this.currentTarget.Instructions,
          isFavorite: this.currentTarget.IsFavorite,
          isSpecialFavorite: this.currentTarget.IsSpecialFavorite,
          IsBackward: this.currentTarget.IsBackward,
          CustomPromptCodeIDs: this.currentTarget.CustomPromptCodeIDs,
          CustomMasteryCriteria:
            !!this.currentTarget.ConsecutiveDays ||
            !!this.currentTarget.ResponsePercentage,
          FirstTrialMustBeIndependent:
            this.currentTarget.FirstTrialMustBeIndependent,
          consecutiveDays: this.currentTarget.ConsecutiveDays,
          responsePercentage: this.currentTarget.ResponsePercentage,
          DateBaselined: this.formatDate(this.currentTarget.DateBaselined),
          DateClosed: this.formatDate(this.currentTarget.DateClosed),
          DateMastered: this.formatDate(this.currentTarget.DateMastered),
          DateOpened: this.formatDate(this.currentTarget.DateOpened),
          QuantityUnit: this.currentTarget.QuantityUnit,
          CanBeNegative: this.currentTarget.CanBeNegative ?? true,
          SkillAreaID: this.currentTarget.SkillAreaID,
          ShouldMasterAutomatic: this.currentTarget.ShouldMasterAutomatic,
          ShouldHandleAfterMasteryAutomatic:
            this.currentTarget.ShouldMoveToMaintenanceAfterMastery != null,
          ShouldMoveToMaintenanceAfterMastery:
            this.currentTarget.ShouldMoveToMaintenanceAfterMastery ?? true,
          MaintenanceCriteriaID: this.currentTarget.MaintenanceCriteriaID,
          OpenSubTargets: this.currentTarget.OpenSubTargets ?? false,
          BSP_SkillAcquisitionGoalID:
            this.currentTarget.BSP_SkillAcquisitionGoalID,
          SkillAcquisitionGoal_Other:
            this.currentTarget.SkillAcquisitionGoal_Other,
          AlarmOption: this.currentTarget.AlarmOption ?? AlarmOption.NoAlarm,
          AlarmSoundOption: this.currentTarget.AlarmSoundOption ?? null,
        });

        this.initialInstructions = this.currentTarget.Instructions ?? '';

        this.updatesFormValidators();
        if (this.currentTarget.TargetType != null) {
          this.updateDurationValidators(this.currentTarget.TargetType);
        }
        this.loadAttachments();
      }

      this.setupMasteringControls();
    });

    if (
      this.addingTargetToProgramID() != null &&
      this.targetForm.get('PatientProgramIDs')?.value == null
    ) {
      this.targetForm
        .get('PatientProgramIDs')
        ?.setValue([this.addingTargetToProgramID()!]);
    }
  }

  toggleShowAllPrograms() {
    this.showAllPrograms.set(!this.showAllPrograms());
  }

  updateDurationValidators(targetType: TargetType | null) {
    const durationControl = this.targetForm.controls.duration;

    if (
      (targetType === TargetType.FluencyRate && !this.isOpenEndedFluencyRate()) ||
      targetType === TargetType.WholePartialInterval
    ) {
      // Duration type targets require duration > 0
      durationControl?.setValidators([
        Validators.required,
        Validators.min(0.016667), // Minimum 1 second (1/60 minutes)
      ]);
    } else {
      // Other target types don't use duration
      durationControl?.clearValidators();
    }

    durationControl?.updateValueAndValidity();
  }

  loadMasteryMaintenanceAndPromptCodes() {
    const maintenanceLevels = this.supervisorApiService.GetMaintananceLevels();
    const maintenanceCriteriaFull =
      this.supervisorApiService.GetCurrentMaintenanceCriteriaFull({
        patientID: this.selectedPatient?.ID!,
      });
    const promptCodes = this.supervisorApiService.GetPromptCodes({
      patientID: this.selectedPatient?.ID!,
    });
    const patientMasteryCriteria = this.supervisorApiService.GetMasteryCriteria({
      patientID: this.selectedPatient?.ID!,
    });
    const globalMasteryCriteria = this.supervisorApiService.GetMasteryCriteria({
      patientID: undefined,
    });
    const patientMaintenanceCriteriaFull =
      this.supervisorApiService.GetMaintenanceCriteriaFull({
        patientID: this.selectedPatient?.ID!,
      });

    forkJoin({
      maintenanceLevels,
      maintenanceCriteriaFull,
      promptCodes,
      patientMasteryCriteria,
      globalMasteryCriteria,
      patientMaintenanceCriteriaFull,
    }).subscribe(
      ({
        maintenanceLevels,
        maintenanceCriteriaFull,
        promptCodes,
        patientMasteryCriteria,
        globalMasteryCriteria,
        patientMaintenanceCriteriaFull,
      }) => {
        this.maintenanceLevels = maintenanceLevels;
        const resolvedPatientMasteryCriteria =
          patientMasteryCriteria as MasteryCriteriaModel | null | undefined;
        const resolvedGlobalMasteryCriteria =
          globalMasteryCriteria as MasteryCriteriaModel | null | undefined;

        this.hasPatientSpecificMasteryCriteria = !!patientMasteryCriteria;
        const patientSpecificMaintenanceCriterias =
          patientMaintenanceCriteriaFull?.MaintenanceCriterias ?? [];
        const hasActivePatientSpecificMaintenanceCriterias =
          patientSpecificMaintenanceCriterias.some(
            (criteria) =>
              criteria.MaintenanceLevelID != null &&
              !criteria.IsLegacyMaintenanceCriteria
          );
        const hasLegacyPatientSpecificMaintenanceCriterias =
          patientSpecificMaintenanceCriterias.some(
            (criteria) => !!criteria.IsLegacyMaintenanceCriteria
          );
        this.hasPatientSpecificMaintenanceCriteria =
          !!patientMaintenanceCriteriaFull?.MaintenanceCriteriaSetting ||
          patientSpecificMaintenanceCriterias.length > 0;
        this.isUsingLegacyMaintenanceCriteria =
          !hasActivePatientSpecificMaintenanceCriterias &&
          hasLegacyPatientSpecificMaintenanceCriterias;
        this.maintenanceCriteriaSettings =
          maintenanceCriteriaFull.MaintenanceCriteriaSetting ?? {};
        const effectiveMaintenanceCriteriaModels =
          maintenanceCriteriaFull.MaintenanceCriterias?.filter(
            (criteria) =>
              criteria.MaintenanceLevelID != null && criteria.IsActive !== false
          ) ?? [];
        this.effectiveMaintenanceCriteriaModels = effectiveMaintenanceCriteriaModels;
        const availableMaintenanceCriteriaModels =
          this.hasPatientSpecificMaintenanceCriteria
            ? patientSpecificMaintenanceCriterias.filter(
                (criteria) =>
                  criteria.MaintenanceLevelID != null &&
                  criteria.IsActive !== false &&
                  (this.isUsingLegacyMaintenanceCriteria
                    ? !!criteria.IsLegacyMaintenanceCriteria
                    : !criteria.IsLegacyMaintenanceCriteria)
              )
            : effectiveMaintenanceCriteriaModels;
        this.maintenanceCriteriaModels =
          this.getSelectableMaintenanceCriteriaModels(
            availableMaintenanceCriteriaModels,
            effectiveMaintenanceCriteriaModels
          );
        this.maintenanceCriterias = this.maintenanceCriteriaModels.map(
          (criteria) => ({
            text: this.getMaintenanceCriteriaOptionLabel(
              criteria,
              maintenanceLevels
            ),
            value: criteria.ID ?? 0,
          })
        );
        this.defaultMasteryCriteria = {
          ResponsePercentage:
            resolvedPatientMasteryCriteria?.ResponsePercentage ??
            resolvedGlobalMasteryCriteria?.ResponsePercentage ??
            80,
          ConsecutiveDays:
            resolvedPatientMasteryCriteria?.ConsecutiveDays ??
            resolvedGlobalMasteryCriteria?.ConsecutiveDays ??
            5,
          FirstTrialIndependent:
            resolvedPatientMasteryCriteria?.FirstTrialIndependent ??
            resolvedGlobalMasteryCriteria?.FirstTrialIndependent ??
            true,
        };
        this.promptCodes = promptCodes?.filter((pc) => !pc.Inactive) ?? [];
        this.updateMasteryCriteriaControls();
        this.updateMaintenanceCriteriaControls();
      }
    );
  }

  isCustomMasteryCriteria() {
    return this.targetForm.get('CustomMasteryCriteria')?.value;
  }

  shouldShowMaintenanceCriteriaAfterMastery() {
    return (
      this.shouldHandleAfterMasteryAutomatically() &&
      !!this.targetForm.get('ShouldMoveToMaintenanceAfterMastery')?.value
    );
  }

  shouldHandleAfterMasteryAutomatically() {
    return !!this.targetForm.get('ShouldHandleAfterMasteryAutomatic')?.value;
  }

  setupMasteringControls() {
    this.targetForm
      .get('CustomMasteryCriteria')
      ?.valueChanges.subscribe(() => this.updateMasteryCriteriaControls());
    this.targetForm
      .get('ShouldHandleAfterMasteryAutomatic')
      ?.valueChanges.subscribe(() => this.updateMaintenanceCriteriaControls());
    this.targetForm
      .get('ShouldMoveToMaintenanceAfterMastery')
      ?.valueChanges.subscribe(() => this.updateMaintenanceCriteriaControls());
    this.targetForm
      .get('targetStatus')
      ?.valueChanges.subscribe(() => this.updateMaintenanceCriteriaControls());

    this.updateMasteryCriteriaControls();
    this.updateMaintenanceCriteriaControls();
  }

  updateMasteryCriteriaControls() {
    const responsePercentageControl = this.targetForm.get('responsePercentage');
    const consecutiveDaysControl = this.targetForm.get('consecutiveDays');
    const firstTrialControl = this.targetForm.get('FirstTrialMustBeIndependent');

    if (this.isCustomMasteryCriteria()) {
      consecutiveDaysControl?.setValidators([Validators.required]);
      responsePercentageControl?.setValidators([Validators.required]);

      if (consecutiveDaysControl?.value == null) {
        consecutiveDaysControl?.setValue(
          this.defaultMasteryCriteria.ConsecutiveDays ?? 5,
          { emitEvent: false }
        );
      }
      if (responsePercentageControl?.value == null) {
        responsePercentageControl?.setValue(
          this.defaultMasteryCriteria.ResponsePercentage ?? 80,
          { emitEvent: false }
        );
      }
      if (firstTrialControl?.value == null) {
        firstTrialControl?.setValue(
          this.defaultMasteryCriteria.FirstTrialIndependent ?? true,
          { emitEvent: false }
        );
      }
    } else {
      consecutiveDaysControl?.removeValidators([Validators.required]);
      responsePercentageControl?.removeValidators([Validators.required]);
      consecutiveDaysControl?.setValue(
        this.defaultMasteryCriteria.ConsecutiveDays ?? 5,
        { emitEvent: false }
      );
      responsePercentageControl?.setValue(
        this.defaultMasteryCriteria.ResponsePercentage ?? 80,
        { emitEvent: false }
      );
      firstTrialControl?.setValue(
        this.defaultMasteryCriteria.FirstTrialIndependent ?? true,
        { emitEvent: false }
      );
    }

    consecutiveDaysControl?.updateValueAndValidity({ emitEvent: false });
    responsePercentageControl?.updateValueAndValidity({ emitEvent: false });
  }

  updateMaintenanceCriteriaControls() {
    const maintenanceCriteriaIdControl = this.targetForm.get('MaintenanceCriteriaID');
    this.inheritedMaintenanceCriteriaIdForDisplay =
      this.getInheritedMaintenanceCriteriaIdForDisplay();

    if (!this.isInMaintenance()) {
      this.inheritedMaintenanceCriteriaIdForDisplay = null;
      maintenanceCriteriaIdControl?.setValue(null, { emitEvent: false });
      return;
    }

    const resolvedSelectedMaintenanceCriteria =
      maintenanceCriteriaIdControl?.value != null
        ? this.getResolvedMaintenanceCriteriaForSelection(
            maintenanceCriteriaIdControl.value,
            this.maintenanceCriteriaModels,
            this.effectiveMaintenanceCriteriaModels
          )
        : null;

    if (
      maintenanceCriteriaIdControl != null &&
      maintenanceCriteriaIdControl.value != null &&
      resolvedSelectedMaintenanceCriteria?.ID != null &&
      resolvedSelectedMaintenanceCriteria.ID !== maintenanceCriteriaIdControl.value
    ) {
      maintenanceCriteriaIdControl.setValue(resolvedSelectedMaintenanceCriteria.ID, {
        emitEvent: false,
      });
      return;
    }

    if (
      maintenanceCriteriaIdControl != null &&
      maintenanceCriteriaIdControl.value == null &&
      this.inheritedMaintenanceCriteriaIdForDisplay != null
    ) {
      maintenanceCriteriaIdControl.setValue(
        this.inheritedMaintenanceCriteriaIdForDisplay,
        { emitEvent: false }
      );
    }
  }

  getMaintenanceCriteriaOptionLabel(
    criteria: MaintenanceCriteriaModel,
    maintenanceLevels: MaintananceLevelModel[]
  ): string {
    const maintenanceLevelName =
      maintenanceLevels.find((level) => level.ID === criteria.MaintenanceLevelID)
        ?.MaintenanceLevel ?? '';
    const sourceLabel = criteria.PatientID
      ? 'patient-specific'
      : 'general';

    return `${maintenanceLevelName} (${sourceLabel})`;
  }

  isPastMasteryPhase(): boolean {
    const targetStatus = this.targetForm.get('targetStatus')?.value;
    return (
      targetStatus === PatientTargetStatus.Mastered ||
      targetStatus === PatientTargetStatus.Maintenance ||
      targetStatus === PatientTargetStatus.Closed ||
      targetStatus === PatientTargetStatus.Fluent
    );
  }

  shouldShowMasteringSection(): boolean {
    const targetStatus = this.targetForm.get('targetStatus')?.value;
    return (
      targetStatus !== PatientTargetStatus.Fluent &&
      targetStatus !== PatientTargetStatus.On_Hold &&
      targetStatus !== PatientTargetStatus.Closed
    );
  }

  get defaultMasteryCriteriaSourceLabel(): string {
    return this.hasPatientSpecificMasteryCriteria
      ? 'Patient-specific mastery criteria'
      : 'General mastery criteria';
  }

  get activeMasteryCriteriaScopeLabel(): string {
    return this.isCustomMasteryCriteria()
      ? 'Target-specific mastery criteria'
      : this.defaultMasteryCriteriaSourceLabel;
  }

  get defaultMaintenanceCriteriaSourceLabel(): string {
    if (this.isUsingLegacyMaintenanceCriteria) {
      return 'Legacy patient-specific maintenance criteria';
    }

    return this.hasPatientSpecificMaintenanceCriteria
      ? 'Patient-specific maintenance criteria'
      : 'General maintenance criteria';
  }

  get masteryCalculationHint(): string {
    if (!this.targetForm.get('ShouldMasterAutomatic')?.value) {
      return 'Mastery will be updated manually for this target. Once it is manually set to mastered, it will follow the "After mastery" rules below.';
    }

    if (this.isCustomMasteryCriteria()) {
      return 'Mastery will be calculated automatically using the target-specific criteria shown below.';
    }

    return `Mastery will be calculated automatically using the default criteria from ${this.defaultMasteryCriteriaSourceLabel.toLowerCase()}.`;
  }

  get masteryCriteriaHint(): string {
    if (this.isCustomMasteryCriteria()) {
      return 'These mastery values are saved on this target only and do not change the patient-specific or general defaults.';
    }

    return `This target inherits ${this.defaultMasteryCriteriaSourceLabel.toLowerCase()}.`;
  }

  get afterMasteryHint(): string {
    if (!this.shouldHandleAfterMasteryAutomatically()) {
      return 'After mastery progression is set to Manual. Spark will not automatically move this target to another status after it is mastered.';
    }

    if (!this.shouldShowMaintenanceCriteriaAfterMastery()) {
      return 'After mastery, this target will skip maintenance and move directly to Fluent.';
    }

    return `After mastery, this target will move into maintenance and start using the maintenance criteria settings.`;
  }

  get currentMaintenanceSettingsHint(): string {
    return this.defaultMaintenanceCriteriaSourceLabel.toLowerCase();
  }

  get maintenanceCriteriaHint(): string {
    if (this.isUsingLegacyMaintenanceCriteria) {
      return 'This target is currently using the patient\'s legacy maintenance criteria from the previous maintenance flow. To update it, open the patient-specific maintenance criteria page and save a new custom configuration.';
    }

    return 'If set, patient-specific maintenance criteria take precedence over general maintenance criteria. These can be adjusted here:';
  }

  get afterMasteryHandlingHint(): string {
    if (this.shouldHandleAfterMasteryAutomatically()) {
      return 'Spark will automatically move the target status after mastery using the progression configured below.';
    }

    return 'Spark will stop automatic post-mastery status changes. The target status selected manually will be kept until changed again.';
  }

  get canAccessPatientMasteryCriteriaSettings(): boolean {
    return this.authService.hasPermission(Permissions.SparkApp_Supervisor);
  }

  get canAccessGeneralMasteryCriteriaSettings(): boolean {
    return this.authService.hasPermission(Permissions.SparkApp_GeneralSettings);
  }

  get canAccessPatientMaintenanceCriteriaSettings(): boolean {
    return this.authService.hasPermission(Permissions.SparkApp_Supervisor);
  }

  get canAccessGeneralMaintenanceCriteriaSettings(): boolean {
    return this.authService.hasPermission(Permissions.SparkApp_GeneralSettings);
  }

  formatDate(date: string | null | undefined): string | null {
    if (!date) return null;
    return formatDate(date, 'yyyy-MM-dd', 'en-US');
  }

  loadTargetTypes() {
    this.targetTypes = [
      { value: TargetType.Anecdotal, text: 'Anecdotal', disabled: false },
      {
        value: TargetType.DiscreteTrialTeaching,
        text: 'DTT (Discrete Trial Teaching)',
        disabled: false,
      },
      {
        value: TargetType.ConditionalDiscrimination,
        text: this.canCreateConditionalDiscriminationTarget()
          ? 'Conditional Discrimination'
          : 'Conditional Discrimination (requires at least 3 targets of type DTT or JumpTo)',
        disabled: !this.canCreateConditionalDiscriminationTarget(),
      },
      { value: TargetType.Duration, text: 'Duration', disabled: false },
      { value: TargetType.Echoic, text: 'Echoic', disabled: false },
      { value: TargetType.FluencyRate, text: 'Fluency Rate', disabled: false },
      { value: TargetType.Frequency, text: 'Frequency', disabled: false },
      {
        value: TargetType.GroupedFrequency,
        text: 'Grouped Frequency',
        disabled: false,
      },
      {
        value: TargetType.JumpTo,
        text: 'Jump To',
        disabled: false,
      },
      { value: TargetType.Currency, text: 'Currency', disabled: false },
      { value: TargetType.Quantity, text: 'Quantity', disabled: false },
      {
        value: TargetType.TaskAnalysis,
        text: 'Task Analysis',
        disabled: false,
      },
      { value: TargetType.Toileting, text: 'Toileting', disabled: false },
      {
        value: TargetType.WholePartialInterval,
        text: 'Whole Partial Interval',
        disabled: false,
      },
    ];
  }

  canCreateConditionalDiscriminationTarget() {
    // We must have at least 3 targets of type DTT or JumpTo to create a Conditional Discrimination target.
    return (
      this.supervisorHelperService
        .targets()
        .filter(
          (t) =>
            t.TargetType === TargetType.DiscreteTrialTeaching ||
            t.TargetType === TargetType.JumpTo
        ).length >= 3
    );
  }

  requiresDuration() {
    return (
      (this.targetForm.get('targetType')?.value === TargetType.FluencyRate &&
        !this.isOpenEndedFluencyRate()) ||
      this.targetForm.get('targetType')?.value ===
        TargetType.WholePartialInterval ||
      this.targetForm.get('targetType')?.value === TargetType.Toileting ||
      this.targetForm.get('targetType')?.value === TargetType.Duration
    );
  }

  requiresSubTargets() {
    return (
      this.targetForm.get('targetType')?.value ===
        TargetType.ConditionalDiscrimination ||
      this.targetForm.get('targetType')?.value ===
        TargetType.GroupedFrequency ||
      this.targetForm.get('targetType')?.value === TargetType.TaskAnalysis
    );
  }

  usesPromptSchedule() {
    return (
      this.targetForm.get('targetType')?.value ===
        TargetType.DiscreteTrialTeaching ||
      this.targetForm.get('targetType')?.value === TargetType.Echoic
    );
  }

  usesCustomPromptCodes() {
    return (
      this.usesPromptSchedule() ||
      this.targetForm.get('targetType')?.value === TargetType.TaskAnalysis
    );
  }

  isQuantity() {
    return this.targetForm.get('targetType')?.value === TargetType.Quantity;
  }

  isCurrency() {
    return this.targetForm.get('targetType')?.value === TargetType.Currency;
  }

  isTaskAnalysis() {
    return this.targetForm.get('targetType')?.value === TargetType.TaskAnalysis;
  }

  isFluencyRate() {
    return this.targetForm.get('targetType')?.value === TargetType.FluencyRate;
  }

  isOpenEndedFluencyRate() {
    return (
      this.isFluencyRate() &&
      this.targetForm.get('FluencyRateTimerMode')?.value ===
        FluencyRateTimerMode.OpenEnded
    );
  }

  isInMaintenance() {
    return this.targetForm.get('targetStatus')?.value === PatientTargetStatus.Maintenance;
  }

  hasAutomaticMasteryOption() {
    return (
      this.targetForm.get('targetType')?.value ===
        TargetType.DiscreteTrialTeaching ||
      this.targetForm.get('targetType')?.value === TargetType.Echoic
    );
  }
  selectedAutomaticMasteryOption() {
    return this.targetForm.get('ShouldMasterAutomatic')?.value;
  }

  private getSelectableMaintenanceCriteriaModels(
    criteriaModels: MaintenanceCriteriaModel[],
    fallbackCriteriaModels: MaintenanceCriteriaModel[]
  ): MaintenanceCriteriaModel[] {
    const availableCriteriaModels = this.isUsingLegacyMaintenanceCriteria
      ? criteriaModels
      : criteriaModels.filter((criteria) =>
          this.isSupportedCurrentFlowMaintenanceLevel(criteria.MaintenanceLevelID)
        );
    const activeCriteria = this.normalizeMaintenanceCriteriaModels(
      availableCriteriaModels.filter((criteria) => !criteria.IsLegacyMaintenanceCriteria)
    );
    const legacyCriteria = this.normalizeMaintenanceCriteriaModels(
      availableCriteriaModels.filter((criteria) => !!criteria.IsLegacyMaintenanceCriteria)
    );
    const selectedCriteria =
      activeCriteria.length > 0 ? activeCriteria : legacyCriteria;
    const selectedCriteriaIds = new Set(
      selectedCriteria
        .map((criteria) => criteria.ID)
        .filter((id): id is number => id != null)
    );
    const currentMaintenanceCriteriaId =
      this.currentTarget?.MaintenanceCriteriaID ??
      this.targetForm.get('MaintenanceCriteriaID')?.value ??
      null;
    if (
      currentMaintenanceCriteriaId != null &&
      !selectedCriteriaIds.has(currentMaintenanceCriteriaId)
    ) {
      const resolvedCurrentMaintenanceCriteria =
        this.getResolvedMaintenanceCriteriaForSelection(
          currentMaintenanceCriteriaId,
          availableCriteriaModels,
          fallbackCriteriaModels
        );

      if (
        resolvedCurrentMaintenanceCriteria?.ID != null &&
        !selectedCriteriaIds.has(resolvedCurrentMaintenanceCriteria.ID)
      ) {
        selectedCriteria.push(resolvedCurrentMaintenanceCriteria);
        selectedCriteriaIds.add(resolvedCurrentMaintenanceCriteria.ID);
      }
    }

    return selectedCriteria.sort((a, b) =>
      this.getMaintenanceCriteriaOptionLabel(a, this.maintenanceLevels).localeCompare(
        this.getMaintenanceCriteriaOptionLabel(b, this.maintenanceLevels)
      )
    );
  }

  private normalizeMaintenanceCriteriaModels(
    criteriaModels: MaintenanceCriteriaModel[]
  ): MaintenanceCriteriaModel[] {
    const criteriaByLevel = new Map<number, MaintenanceCriteriaModel>();

    for (const criteria of criteriaModels) {
      if (criteria.MaintenanceLevelID == null) {
        continue;
      }

      const existingCriteria = criteriaByLevel.get(criteria.MaintenanceLevelID);
      if (
        !existingCriteria ||
        (criteria.ID ?? Number.MAX_SAFE_INTEGER) <
          (existingCriteria.ID ?? Number.MAX_SAFE_INTEGER)
      ) {
        criteriaByLevel.set(criteria.MaintenanceLevelID, criteria);
      }
    }

    return Array.from(criteriaByLevel.values());
  }

  private isSupportedCurrentFlowMaintenanceLevel(
    maintenanceLevelId: number | undefined
  ): boolean {
    if (maintenanceLevelId == null) {
      return false;
    }

    const maintenanceLevelName = this.maintenanceLevels.find(
      (level) => level.ID === maintenanceLevelId
    )?.MaintenanceLevel;

    return this.supportedMaintenanceLevelNames.has(maintenanceLevelName ?? '');
  }

  private getInheritedMaintenanceCriteriaIdForDisplay(): number | null {
    if (
      !this.currentTarget?.ID ||
      this.currentTarget.MaintenanceCriteriaID != null ||
      !this.isInMaintenance()
    ) {
      return null;
    }

    const inheritedMaintenanceLevelId = this.currentTarget.WasMasteredFromProbe
      ? this.maintenanceCriteriaSettings.MaintenanceLevelIDWhenFromProbe
      : this.maintenanceCriteriaSettings.MaintenanceLevelIDWhenFromAquisition;

    if (inheritedMaintenanceLevelId == null) {
      return null;
    }

    return (
      this.maintenanceCriteriaModels.find(
        (criteria) => criteria.MaintenanceLevelID === inheritedMaintenanceLevelId
      )?.ID ?? null
    );
  }

  private getResolvedMaintenanceCriteriaForSelection(
    maintenanceCriteriaId: number,
    availableCriteriaModels: MaintenanceCriteriaModel[],
    fallbackCriteriaModels: MaintenanceCriteriaModel[]
  ): MaintenanceCriteriaModel | undefined {
    const directMatch = availableCriteriaModels.find(
      (criteria) => criteria.ID === maintenanceCriteriaId
    );
    if (directMatch) {
      return directMatch;
    }

    const sourceCriteria =
      fallbackCriteriaModels.find((criteria) => criteria.ID === maintenanceCriteriaId) ??
      availableCriteriaModels.find((criteria) => criteria.ID === maintenanceCriteriaId);
    if (!sourceCriteria?.MaintenanceLevelID) {
      return undefined;
    }

    return availableCriteriaModels.find(
      (criteria) => criteria.MaintenanceLevelID === sourceCriteria.MaintenanceLevelID
    );
  }

  updatesFormValidators() {
    this.updateDurationValidators(this.targetForm.controls.targetType.value);

    this.targetForm.controls.PromptScheduleID.updateValueAndValidity();
  }

  onCancel() {
    this.currentTarget = undefined;
    this.newSubTargets = []; // Clear new subtargets
    if (this.targetForm) this.targetForm.reset();
    this.close.emit(false);
  }

  onSubmit() {
    if (!this.isLoading) {
      if (this.selectedSubTargets && this.selectedSubTargets.length > 0) {
        this.alertService
          .confirm(
            'Propagate Status',
            'You´re changing this target status. Do you want to also change its subtargets statuses?'
          )
          .subscribe((result) => {
            this.saveTarget(false, result);
          });
      } else {
        this.saveTarget(false, false);
      }
    }
  }

  saveTarget(propagateArchieve: boolean, propagateStatus: boolean) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.targetForm.markAllAsTouched();

      if (this.targetForm.invalid) {
        const errors = Utils.getFormErrors(this.targetForm);
        if (errors.length > 0) {
          this.alertService.error(errors.join(' \n'));
          this.isLoading = false;
          return;
        }
      }

      // Validate new subtargets
      if (
        this.newSubTargets.some(
          (target) => !target.TargetName || target.TargetName.trim() === ''
        )
      ) {
        this.alertService.error('All new sub targets must have a name.');
        this.isLoading = false;
        return;
      }

      var formValue = this.targetForm.getRawValue();
      console.log(formValue);
      if (!formValue.CustomMasteryCriteria) {
        formValue.consecutiveDays = null;
        formValue.responsePercentage = null;
      }
      if (!this.isInMaintenance()) {
        formValue.MaintenanceCriteriaID = null;
      } else if (
        this.currentTarget?.ID &&
        this.currentTarget.MaintenanceCriteriaID == null &&
        formValue.MaintenanceCriteriaID ===
          this.inheritedMaintenanceCriteriaIdForDisplay
      ) {
        formValue.MaintenanceCriteriaID = null;
      }
      if (formValue.duration) {
        formValue.duration = this.minutesToSeconds(formValue.duration);
      }
      if (formValue.targetType === TargetType.FluencyRate &&
          formValue.FluencyRateTimerMode === FluencyRateTimerMode.OpenEnded) {
        formValue.duration = null;
        formValue.AlarmOption = AlarmOption.NoAlarm;
        formValue.AlarmSoundOption = null;
      }

      // Merge existing subtargets with new ones
      const existingSubTargets: SubTargetsSimpleModel[] =
        this.selectedSubTargets
          .filter((target) => !!target.ID)
          .map((target) => ({
            ID: target.ID!,
            TargetName: target.TargetName!,
            ForceOpen: (target as any).ForceOpen ?? false,
          }));

      const newSubTargetsForSave: SubTargetsSimpleModel[] =
        this.newSubTargets.map((target) => ({
          ID: undefined, // New targets don't have ID yet
          TargetName: target.TargetName,
          ForceOpen: target.ForceOpen,
        }));

      const allSubTargets = [...existingSubTargets, ...newSubTargetsForSave];

      const shouldHandleAfterMasteryAutomatic =
        formValue.ShouldHandleAfterMasteryAutomatic ?? true;
      const shouldMoveToMaintenanceAfterMastery =
        shouldHandleAfterMasteryAutomatic
          ? (formValue.ShouldMoveToMaintenanceAfterMastery ?? true)
          : null;

      this.currentTarget = {
        ...this.currentTarget,
        ...formValue,
        SubTargets: allSubTargets,
        FluencyRateTimerMode:
          formValue.FluencyRateTimerMode ?? FluencyRateTimerMode.FixedDuration,
        ShouldMasterAutomatic: formValue.ShouldMasterAutomatic ?? false,
        ShouldMoveToMaintenanceAfterMastery: shouldMoveToMaintenanceAfterMastery,
        FirstTrialMustBeIndependent:
          formValue.FirstTrialMustBeIndependent ?? false,
        AlarmOption: formValue.AlarmOption ?? AlarmOption.NoAlarm,
        AlarmSoundOption: formValue.AlarmSoundOption ?? null,
      };
      console.info(this.currentTarget);

      this.supervisorApiService
        .SavePatientTarget({
          body: {
            PatientTarget: this.currentTarget,
            PropagateArchieve: propagateArchieve,
            PropagateStatus: propagateStatus,
          },
        })
        .subscribe(
          (savedItem) => {
            this.isLoading = false;

            this.supervisorHelperService.lastCreatedTarget.set(savedItem);

            this.currentTarget = undefined;
            this.newSubTargets = []; // Clear new subtargets
            this.targetForm.reset();
            this.alertService.success('Target saved successfully');
            this.close.emit(true);
          },
          (err) => {
            this.isLoading = false;
            this.alertService.error(
              Utils.getErrorMessageFromErrorResponse(err)
            );
            this.close.emit(false);
          }
        );
    }
  }

  // Add these methods to the component class
  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  archiveButtonClicked() {
    if (!this.isLoading) {
      this.currentTarget!.IsArchived = !this.currentTarget!.IsArchived;
      if (this.selectedSubTargets && this.selectedSubTargets.length > 0) {
        this.alertService
          .confirm(
            'Propagate Archive',
            'You´re changing this target archive status. Do you want to also change its subtargets archive statuses?'
          )
          .subscribe((result) => {
            this.saveTarget(result, false);
          });
      } else {
        this.saveTarget(false, false);
      }
    }
  }

  onSkillAreaChange(event: any) {
    console.log('skill area changed: ', event);
    this.selectedSkillAreaID.set(event.value);
    const tourEvent = new Event('skill-area-selected');
    document.getElementById('addTargetForm')?.dispatchEvent(tourEvent);
  }

  onSkillAreaDropdownClicked() {
    const event = new Event('skill-area-dropdown-clicked');
    document.getElementById('addTargetForm')?.dispatchEvent(event);
  }

  onProgramsDropdownClicked() {
    const event = new Event('programs-dropdown-clicked');
    document.getElementById('addTargetForm')?.dispatchEvent(event);
  }

  onTargetTypeDropdownClicked() {
    const event = new Event('target-type-dropdown-clicked');
    document.getElementById('addTargetForm')?.dispatchEvent(event);
  }

  getFilteredPlaylists() {
    return this.supervisorHelperService
      .playlists()
      .filter((playlist) => !playlist.IsDeleted);
  }

  getFilteredTargets() {
    return this.supervisorHelperService
      .targets()
      .filter((target) => !target.IsDeleted);
  }

  onAddSubTarget() {
    this.showingAvailableTargets = !this.showingAvailableTargets;
  }

  onInstructionsChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.targetForm.get('instructions')?.setValue(data);
    }
  }

  selectedSkillAcquisitionGoal() {
    const selectedGoalId =
      this.targetForm.get('BSP_SkillAcquisitionGoalID')?.value ??
      this.currentTarget?.BSP_SkillAcquisitionGoalID;
    if (selectedGoalId) {
      return this.skillAcquisitionGoals?.find(
        (goal) => goal.ID === selectedGoalId
      );
    }
    return null;
  }

  // Methods for managing new subtargets
  addNewSubTarget() {
    this.newSubTargets.push({
      TargetName: '',
      ForceOpen: false,
    });
  }

  removeNewSubTarget(index: number) {
    this.newSubTargets.splice(index, 1);
  }

  // Attachment functions

  toggleShowAddAttachments(): void {
    this.showAddAttachments = !this.showAddAttachments;
  }

  openAttachmentView(att: GeneralAttachmentModel): void {
    this.doShowAttachmentView = true;
    this.selectedAttachment = att;
  }

  getImageUrl(att: GeneralAttachmentModel): string {
    var url = 'data:' + att.FileType + ';base64,' + att.ImageThumbnail;
    return url;
  }

  closeAddAttachments() {
    this.showAddAttachments = false;
    this.loadAttachments();
  }

  loadAttachments() {
    this.isLoadingAttachments = true;
    this.supervisorApiService
      .GetTargetAttachments({
        targetID: this.targetID,
      })
      .subscribe({
        next: (results) => {
          this.attachmentList = results;
          this.isLoadingAttachments = false;
        },
        error: (error) => {
          console.error('Error loading patient instruction attachments', error);
          this.alertService.error(
            'Failed to load patient instruction attachments'
          );
          this.isLoadingAttachments = false;
        },
      });
  }

  closeAttachmentView() {
    this.doShowAttachmentView = false;
    this.selectedAttachment = null;
    this.loadAttachments();
  }

  shouldShowAlarmOptions() {
    const targetType = this.targetForm.get('targetType')?.value;
    return (
      targetType === TargetType.Duration ||
      (targetType === TargetType.FluencyRate && !this.isOpenEndedFluencyRate()) ||
      targetType === TargetType.Toileting ||
      targetType === TargetType.WholePartialInterval
    );
  }

  updateAlarmControlsForTargetType() {
    if (!this.shouldShowAlarmOptions()) {
      this.targetForm.controls.AlarmOption.setValue(AlarmOption.NoAlarm, { emitEvent: false });
      this.targetForm.controls.AlarmSoundOption.setValue(null, { emitEvent: false });
    }
  }

  playAlarmSound(event: any) {
    const selectedSoundToPlay = event.value;
    const audio = new Audio(`assets/sounds/alarm-${selectedSoundToPlay}.mp3`);
    audio.play();
    setTimeout(() => {
      audio.pause();
    }, 5000);
  }

  shouldHideStartingBalance() {
    var targetType = this.targetForm.get('targetType')?.value;
    if (targetType === TargetType.Currency && this.currentTarget?.ID) {
      return true;
    }
    return false;
  }
}
